import React, { useState } from "react";
import { 
  Briefcase, 
  Send, 
  Shield, 
  Zap, 
  BookOpen, 
  Clock, 
  Globe, 
  Laptop, 
  UploadCloud, 
  FileText, 
  X, 
  AlertCircle, 
  CheckCircle2, 
  Loader2 
} from "lucide-react";
import SEO from "../components/SEO";
import "./Careers.css";

const Careers = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    experience: "",
    currentCompany: "",
    currentCtc: "",
    expectedCtc: "",
    noticePeriod: "",
    skills: "",
    linkedin: "",
    portfolio: "",
    role: "Web Development",
    message: ""
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateAndSetFile = (file) => {
    setFileError("");
    setSubmitError("");
    if (!file) return;

    const allowedExtensions = [".pdf", ".docx"];
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    const allowedMimetypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/x-zip-compressed"
    ];

    if (!allowedExtensions.includes(fileExt) && !allowedMimetypes.includes(file.type)) {
      setFileError("Invalid file type. Only PDF and DOCX files are allowed.");
      return;
    }

    if (file.size === 0) {
      setFileError("The uploaded file is empty.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setFileError("File size exceeds the 10 MB limit.");
      return;
    }

    setResumeFile(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const removeFile = () => {
    setResumeFile(null);
    setFileError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!resumeFile) {
      setFileError("Please upload your resume (PDF or DOCX).");
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });
      data.append("coverLetter", formData.message);
      data.append("resume", resumeFile);

      const response = await fetch("/.netlify/functions/apply", {
        method: "POST",
        body: data
      });

      const contentType = response.headers.get("content-type") || "";
      let result;

      if (contentType.includes("application/json")) {
        result = await response.json();
      } else {
        throw new Error(
          "Netlify serverless function is not active locally. Please run 'npx netlify dev' (or 'npm run dev') in your terminal to test functions locally."
        );
      }

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to submit application. Please try again.");
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || "An error occurred while submitting your application.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      location: "",
      experience: "",
      currentCompany: "",
      currentCtc: "",
      expectedCtc: "",
      noticePeriod: "",
      skills: "",
      linkedin: "",
      portfolio: "",
      role: "Web Development",
      message: ""
    });
    setResumeFile(null);
    setFileError("");
    setSubmitError("");
    setSubmitted(false);
  };

  return (
    <>
      <SEO 
        title="Careers | Work With Us | Vektrixo Digital" 
        description="Join our team of elite developers and QA engineers. Explore our culture, perks, and submit an application for tech roles at Vektrixo Digital."
        keywords="Vektrixo Digital Careers, Software Engineer Jobs Pune, QA Automation Jobs, React Developer Remote, Tech Jobs India"
        canonical="https://www.vektrixo.com/careers"
      />
      <div className="careers-container">
        {/* 1. HERO */}
        <section className="careers-hero">
          <span className="badge">Careers</span>
          <h1>Build the Future of <span className="highlight">Digital Products</span></h1>
          <p>
            We are a tight-knit team of engineers, designer minds, and QA specialists building high-performance websites and robust systems. Shape your career with absolute autonomy and technical excellence.
          </p>
          <a href="#apply" className="btn-primary">
            <Briefcase size={18} /> View Opportunities
          </a>
        </section>

        {/* 2. CULTURE & VALUES */}
        <section className="culture-section">
          <h2>Our Core <span className="highlight">Values</span></h2>
          <p className="section-intro">What drives us every single day at Vektrixo Digital.</p>
          
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <Zap size={32} color="#38bdf8" />
              </div>
              <h3>Engineering First</h3>
              <p>We care deeply about clean code, performance, accessibility, and stellar QA practices. We build things right the first time.</p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <Clock size={32} color="#38bdf8" />
              </div>
              <h3>Complete Autonomy</h3>
              <p>No micromanagement. We focus purely on outcomes and quality. You decide when and how you deliver your best work.</p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <BookOpen size={32} color="#38bdf8" />
              </div>
              <h3>Continuous Growth</h3>
              <p>We actively fund courses, technical books, and industry certifications. When you grow, the company grows with you.</p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <Shield size={32} color="#38bdf8" />
              </div>
              <h3>Radical Transparency</h3>
              <p>Open communication channels, flat team structures, and collaborative feedback are the foundations of our workflow.</p>
            </div>
          </div>
        </section>

        {/* 3. BENEFITS & PERKS */}
        <section className="benefits-section">
          <h2>Perks & <span className="highlight">Benefits</span></h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <Globe size={24} className="benefit-icon" />
              <div>
                <h4>100% Remote Work</h4>
                <p>Work from the comfort of your home, co-working space, or anywhere with a stable internet connection.</p>
              </div>
            </div>

            <div className="benefit-item">
              <Clock size={24} className="benefit-icon" />
              <div>
                <h4>Flexible Hours</h4>
                <p>Align work with your biological peak hours. We respect your personal time and off-work boundaries.</p>
              </div>
            </div>

            <div className="benefit-item">
              <Laptop size={24} className="benefit-icon" />
              <div>
                <h4>Latest Tech & Tools</h4>
                <p>Get access to top-tier developer tools, subscriptions, and testing infrastructure needed to excel.</p>
              </div>
            </div>

            <div className="benefit-item">
              <BookOpen size={24} className="benefit-icon" />
              <div>
                <h4>Learning Stipend</h4>
                <p>An annual budget dedicated entirely to your learning resources, online bootcamps, and professional courses.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRENT OPENINGS & GENERAL APPLICATION */}
        <section className="openings-section" id="apply">
          <div className="openings-card">
            <h2>Current <span className="highlight">Openings</span></h2>
            <div className="status-notice">
              <p>
                We do not have any active job openings at this moment. However, we are constantly looking to connect with talented developers and QA experts for future full-time roles.
              </p>
              <p className="highlight-text">
                Submit a general application below, and we will contact you as soon as a fitting position opens up!
              </p>
            </div>

            {submitted ? (
              <div className="success-message">
                <div className="success-icon-wrapper">
                  <CheckCircle2 size={54} color="#38bdf8" />
                </div>
                <h3>Application Received!</h3>
                <p>Thank you for reaching out. We have securely processed your resume and stored your application details. Our team will review your profile and contact you when a matching opportunity arises.</p>
                <button className="btn-outline" onClick={resetForm}>Submit Another Application</button>
              </div>
            ) : (
              <form className="careers-form" onSubmit={handleSubmit}>
                {submitError && (
                  <div className="error-banner">
                    <AlertCircle size={20} />
                    <span>{submitError}</span>
                  </div>
                )}

                {/* Personal Info Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                {/* Contact & Location Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="location">Current Location *</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="City, Country"
                      required
                    />
                  </div>
                </div>

                {/* Role & Experience Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="role">Role of Interest *</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="Web Development">Frontend / Full-stack Developer</option>
                      <option value="QA Engineering">QA Automation / Manual Tester</option>
                      <option value="UI/UX Design">UI/UX Designer</option>
                      <option value="Backend Engineering">Backend / Cloud Engineer</option>
                      <option value="Other">Other / General Interest</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="experience">Years of Experience *</label>
                    <input
                      type="text"
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      placeholder="e.g. 3+ Years"
                      required
                    />
                  </div>
                </div>

                {/* Company & Compensation Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="currentCompany">Current Company</label>
                    <input
                      type="text"
                      id="currentCompany"
                      name="currentCompany"
                      value={formData.currentCompany}
                      onChange={handleChange}
                      placeholder="Acme Corp (Optional)"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="noticePeriod">Notice Period *</label>
                    <input
                      type="text"
                      id="noticePeriod"
                      name="noticePeriod"
                      value={formData.noticePeriod}
                      onChange={handleChange}
                      placeholder="e.g. Immediate / 30 Days"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="currentCtc">Current CTC</label>
                    <input
                      type="text"
                      id="currentCtc"
                      name="currentCtc"
                      value={formData.currentCtc}
                      onChange={handleChange}
                      placeholder="e.g. $80,000 / annum"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="expectedCtc">Expected CTC</label>
                    <input
                      type="text"
                      id="expectedCtc"
                      name="expectedCtc"
                      value={formData.expectedCtc}
                      onChange={handleChange}
                      placeholder="e.g. $100,000 / annum"
                    />
                  </div>
                </div>

                {/* Skills & Links */}
                <div className="form-group">
                  <label htmlFor="skills">Primary Skills & Technologies *</label>
                  <input
                    type="text"
                    id="skills"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    placeholder="React, Node.js, TypeScript, Tailwind, Cypress, AWS..."
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="linkedin">LinkedIn Profile URL</label>
                    <input
                      type="url"
                      id="linkedin"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="portfolio">Portfolio / GitHub URL</label>
                    <input
                      type="url"
                      id="portfolio"
                      name="portfolio"
                      value={formData.portfolio}
                      onChange={handleChange}
                      placeholder="https://github.com/username"
                    />
                  </div>
                </div>

                {/* Cover Letter */}
                <div className="form-group">
                  <label htmlFor="message">Cover Letter / Tell Us About Yourself</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Briefly describe your background, notable projects, and what value you'll bring to the team."
                  ></textarea>
                </div>

                {/* Secure Resume Upload Area */}
                <div className="form-group">
                  <label>Resume Upload (PDF or DOCX, max 10 MB) *</label>

                  {!resumeFile ? (
                    <div
                      className={`upload-dropzone ${isDragging ? "dragging" : ""} ${fileError ? "error" : ""}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        id="resume"
                        name="resume"
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileChange}
                        className="file-input-hidden"
                      />
                      <label htmlFor="resume" className="upload-label">
                        <div className="upload-icon-wrapper">
                          <UploadCloud size={32} color="#38bdf8" />
                        </div>
                        <span className="upload-primary-text">
                          <strong>Click to upload</strong> or drag & drop your resume
                        </span>
                        <span className="upload-secondary-text">PDF or DOCX (Max 10 MB)</span>
                      </label>
                    </div>
                  ) : (
                    <div className="file-preview-card">
                      <div className="file-info">
                        <FileText size={28} color="#38bdf8" className="file-icon" />
                        <div className="file-details">
                          <span className="file-name">{resumeFile.name}</span>
                          <span className="file-size">{formatFileSize(resumeFile.size)}</span>
                        </div>
                        <span className="file-badge">
                          {resumeFile.name.split(".").pop().toUpperCase()}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={removeFile}
                        title="Remove file"
                        disabled={isSubmitting}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}

                  {fileError && (
                    <div className="field-error-text">
                      <AlertCircle size={14} /> {fileError}
                    </div>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="submit-btn" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="spinner-icon" /> Processing Application...
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Submit Application
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Careers;
